import { downloadVideo } from '@src/services/videoDownloadService';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import DownloadIcon from '@src/svgs/DownloadIcon';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { extractErrorInfo } from '../../utility/error';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';

const logger = LogManager.getLogger('component:DownloadButton');

export const DownloadButton: React.FC = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const [isDownloading, setIsDownloading] = useState(false);

  const currentVideo = videoCreator.currentVideo;
  const story = videoCreator.story;
  const currentAspectRatio = videoCreator.getCurrentAspectRatio();

  const isPublished = !!currentVideo?.videoFilePrimary;
  const downloadUrl = currentVideo?.videoFilePrimary?.url;

  const handleDownload = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentVideo || !downloadUrl || !isPublished) {
      logger.warn('Video is not published or URL is missing');
      return;
    }

    try {
      setIsDownloading(true);

      await downloadVideo({
        url: downloadUrl,
        metadata: {
          clipId: currentVideo.id,
          storyId: story?.id,
          storyTitle: story?.title,
          videoTitle: currentVideo.title,
          aspectRatio: currentAspectRatio,
          sourcePlatform: 'creator-studio',
        },
      });
    } catch (error) {
      logger.error('Download failed:', extractErrorInfo(error));
    } finally {
      setIsDownloading(false);
    }
  };

  // Disable button if video is not published or already downloading
  const isDisabled =
    !isPublished || isDownloading || !videoCreator.isVideoCreatorReady;

  return (
    <Main
      active={!isDisabled}
      disabled={isDisabled}
      onClick={isDisabled ? undefined : handleDownload}
      data-tooltip-id="download-video-tooltip"
      data-tooltip-content="Download published video"
    >
      <DownloadIcon width="16" height="16" strokeColor="currentColor" />
      <Tooltip
        id="download-video-tooltip"
        style={COMPACT_TOOLTIP_STYLE}
        place="bottom"
        positionStrategy="fixed"
      />
    </Main>
  );
});

const Main = styled.div<{ active?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: var(--radius-button);
  color: ${({ disabled }) =>
    disabled ? 'var(--text-disabled)' : 'var(--accent)'};
  background-color: transparent;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: all 150ms ease-out;

  &:hover {
    background-color: ${({ disabled }) =>
      disabled ? 'transparent' : 'var(--surface-elevated)'};
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }
`;
